import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, isToday, differenceInDays, addDays } from 'date-fns';
import './DateSlider.css';

/**
 * DateSlider component that displays a slider with a moving window of dates
 * - Shows up to 5 days before and after the selected date (window of 11)
 * - Always shows today if possible
 * - Only allows going back 10 days from today, and up to the latest available date (can be in the future)
 */
const DateSlider = ({ dates, selectedDate, onChange, loading }) => {
  const [availableDates, setAvailableDates] = useState([]);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [windowDates, setWindowDates] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [clickArmed, setClickArmed] = useState(true);
  const [sliderArmed, setSliderArmed] = useState(true);
  const [pendingSliderPosition, setPendingSliderPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // --- EDGE SCROLLING STATE ---
  const [edgeScrollDir, setEdgeScrollDir] = useState(null);
  const edgeScrollRef = React.useRef();

  useEffect(() => {
    if (dates && dates.length > 0) {
      let filtered = dates.filter(d => d !== 'all').sort();
      setAvailableDates(filtered);
    }
  }, [dates]);

  // Compute window of 11 days centered on selectedDate
  useEffect(() => {
    if (!selectedDate) return;
    const baseDate = parseISO(selectedDate);
    const windowed = [];
    for (let offset = -5; offset <= 5; offset++) {
      const d = addDays(baseDate, offset);
      windowed.push(format(d, 'yyyy-MM-dd'));
    }
    setWindowDates(windowed);
    // Only update the selected dot when not loading
    if (!loading) {
      setSliderPosition(5); // always center
    }
  }, [selectedDate, loading]);

  // When loading finishes and a new date is selected, update the dot
  useEffect(() => {
    if (!loading && windowDates.length > 0 && selectedDate) {
      const idx = windowDates.indexOf(selectedDate);
      if (idx !== -1) setSliderPosition(idx);
    }
  }, [loading, windowDates, selectedDate]);

  // --- EDGE SCROLLING EFFECT ---
  useEffect(() => {
    if (!isDragging || !edgeScrollDir) return;
    const scroll = () => {
      setSliderPosition(pos => {
        let next = pos;
        if (edgeScrollDir === 'left' && pos > 0) next = pos - 1;
        if (edgeScrollDir === 'right' && pos < windowDates.length - 1) next = pos + 1;
        setPendingSliderPosition(next);
        return next;
      });
      edgeScrollRef.current = setTimeout(scroll, 100);
    };
    edgeScrollRef.current = setTimeout(scroll, 300);
    return () => clearTimeout(edgeScrollRef.current);
  }, [isDragging, edgeScrollDir, windowDates.length]);

  // --- CLEANUP EDGE SCROLLING ---
  useEffect(() => () => clearTimeout(edgeScrollRef.current), []);

  // Handle slider change
  const handleSliderChange = useCallback((e) => {
    const newPosition = parseInt(e.target.value, 10);
    setPendingSliderPosition(newPosition);
    setSliderPosition(newPosition); // update UI for hover dot
  }, []);

  const commitSliderChange = useCallback((finalPosition) => {
    if (windowDates.length > 0 && finalPosition >= 0 && finalPosition < windowDates.length) {
      const newDate = windowDates[finalPosition];
      if (availableDates.includes(newDate) && newDate !== selectedDate) {
        onChange(newDate);
      }
    }
  }, [windowDates, availableDates, onChange, selectedDate]);

  const handleDateMouseDown = useCallback(() => {
    setClickArmed(true);
  }, []);

  const handleDateMouseUp = useCallback(() => {
    setClickArmed(true);
  }, []);

  const handleDateClick = useCallback((index) => {
    if (!clickArmed) return;
    setClickArmed(false);
    setTimeout(() => setClickArmed(true), 1000); // 1s cooldown
    setSliderPosition(index);
    if (windowDates.length > 0 && index >= 0 && index < windowDates.length) {
      const newDate = windowDates[index];
      if (availableDates.includes(newDate) && newDate !== selectedDate) {
        onChange(newDate);
      }
    }
  }, [windowDates, availableDates, onChange, selectedDate, clickArmed]);

  // Handle date hover
  const handleDateMouseEnter = useCallback((index) => {
    setHoveredIndex(index);
  }, []);
  const handleDateMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  // --- MOUSE MOVE HANDLER FOR EDGE SCROLL ---
  const handleSliderMouseMove = useCallback((e) => {
    const rect = e.target.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(percent * (windowDates.length - 1));
    setHoveredIndex(idx);
    if (isDragging) {
      setPendingSliderPosition(idx);
      // Edge scroll: if near left/right edge, start scrolling
      if (percent < 0.05) setEdgeScrollDir('left');
      else if (percent > 0.95) setEdgeScrollDir('right');
      else setEdgeScrollDir(null);
    }
  }, [isDragging, windowDates.length]);

  const handleSliderMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleSliderMouseUp = useCallback(() => {
    setIsDragging(false);
    setEdgeScrollDir(null);
    if (pendingSliderPosition !== null) {
      commitSliderChange(pendingSliderPosition);
      setPendingSliderPosition(null);
    }
  }, [commitSliderChange, pendingSliderPosition]);

  // Format date label
  const formatDateLabel = useCallback((date, idx) => {
    if (!date || date === 'all' || !date.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)) {
      return <div className="date-label">Invalid</div>;
    }
    try {
      const dateObj = parseISO(date);
      const isCurrentDate = isToday(dateObj);
      const isAvailable = availableDates.includes(date);
      const isHovered = idx === hoveredIndex;
      return (
        <div
          className={`date-label ${isCurrentDate ? 'today' : ''} ${isAvailable ? '' : 'disabled'}${isHovered ? ' hovered' : ''}`}
          onClick={() => isAvailable && handleDateClick(idx)}
          onMouseDown={handleDateMouseDown}
          onMouseUp={handleDateMouseUp}
          onMouseEnter={() => handleDateMouseEnter(idx)}
          onMouseLeave={handleDateMouseLeave}
          style={{ cursor: isAvailable ? 'pointer' : 'not-allowed', opacity: isAvailable ? 1 : 0.4 }}
        >
          {format(dateObj, 'MMM d')}
        </div>
      );
    } catch (error) {
      return <div className="date-label">Error</div>;
    }
  }, [availableDates, handleDateClick, hoveredIndex, handleDateMouseEnter, handleDateMouseLeave]);

  // --- SELECTED DOT LOGIC: Only show after loading and after mouse is released, never update during drag ---
  const showSelectedDot = !isDragging && !loading && selectedDate && windowDates.includes(selectedDate);
  const effectiveSliderPosition = showSelectedDot ? windowDates.indexOf(selectedDate) : null;

  // --- SLIDER VALUE: Always show the selected date's index (thumb never moves during drag, only hover dot moves) ---
  const sliderValue = selectedDate && windowDates.includes(selectedDate) ? windowDates.indexOf(selectedDate) : 0;

  if (!windowDates.length) return (
    <div className="date-slider-container">
      <div className="no-dates-message">No available forecast dates.</div>
    </div>
  );

  // Calculate if the date is before or after today for styling
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todayIdx = windowDates.indexOf(todayStr);
  const getDatePosition = (index) => {
    if (todayIdx === -1) return '';
    if (index < todayIdx) return 'past';
    if (index > todayIdx) return 'future';
    return 'today';
  };

  return (
    <div className="date-slider-container">
      <div className="slider-container">
        <div className="date-labels">
          {windowDates.map((date, index) => (
            <div
              key={date}
              className={`date-label-container ${index === sliderPosition ? 'selected' : ''} ${getDatePosition(index)}`}
              style={{
                left: `${(index / (windowDates.length - 1)) * 100}%`,
                transform: 'translateX(-50%)'
              }}
              onMouseEnter={() => handleDateMouseEnter(index)}
              onMouseLeave={handleDateMouseLeave}
            >
              {formatDateLabel(date, index)}
            </div>
          ))}
        </div>
        <input
          type="range"
          min="0"
          max={windowDates.length - 1}
          value={sliderValue}
          onChange={handleSliderChange}
          className="date-slider"
          onMouseDown={handleSliderMouseDown}
          onMouseUp={handleSliderMouseUp}
          onTouchStart={handleSliderMouseDown}
          onTouchEnd={handleSliderMouseUp}
          onMouseMove={handleSliderMouseMove}
          onMouseLeave={handleDateMouseLeave}
        />
        <div className="timeline">
          {todayIdx !== -1 && <div className="today-marker" style={{ left: `${(todayIdx / (windowDates.length - 1)) * 100}%` }}>Today</div>}
          {/* Hover dot on timeline */}
          {hoveredIndex !== null && hoveredIndex >= 0 && hoveredIndex < windowDates.length && (
            <div
              className="timeline-hover-dot"
              style={{ left: `${(hoveredIndex / (windowDates.length - 1)) * 100}%`, cursor: 'pointer' }}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default DateSlider;